import { createContext, useCallback } from 'react'
import { useMutation } from 'react-query'
import { generatePath, useHistory, useLocation } from 'react-router-dom'

import { useAppApi, CreditsApi, useMyProfile, UserAddress } from 'api'
import { useYupForm } from 'hooks/form'
import { useContextWithCheck } from 'hooks'
import { useTranslation } from 'hooks/useTranslation'
import { appRoutes } from 'routes'
import { fiatAssetMain } from 'config/currencies'
import { fixedDecimals } from 'utils/formatters'

import { BuySchema, buySchema, EXCHANGE_RATE } from './BuyContext.config'

import { BuyContextProps, BuyProviderProps, OnChangeAmount, OnChangePromo } from '.'

const BuyContext = createContext<BuyContextProps | undefined>(undefined)

export const BuyProvider = ({
  children,
  src_currency = fiatAssetMain,
}: BuyProviderProps): JSX.Element => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { data: profile } = useMyProfile()

  const form = useYupForm(buySchema(t))
  const { setValue } = form
  const transactionApi = useAppApi(CreditsApi)

  const submitMutation = useMutation(
    (data: BuySchema) => {
      if (!data.dstAmount) throw Error('Amount is required')
      // const dstAmount = (data?.dstAmount ?? 0) * EXCHANGE_RATE
      const amount = fixedDecimals(data?.dstAmount, 0)
      return transactionApi.makePurchase({ amount })
    },
    {
      onSuccess: data => {
        const { redirect_url } = data
        window.location.href = redirect_url
      }
    },
  )

  const handleBillingNextSubmit = (address: UserAddress) => {
    const resendForm =  form.handleSubmit(data => {
      return submitMutation.mutate(data)
    })

    // TODO  validate billing address info not only country before submit
    if (address?.country) {
      resendForm().then(() => {})
    }
  }
  const handleSubmit = form.handleSubmit(data => {
    // TODO  validate billing address info not only country before submit
    if (!profile?.country || !profile.address_1) {
      history.push({
        pathname: generatePath(appRoutes.buyBillingInfo.path),
        state: {
          from: location,
        },
      })

      return
    }
    return submitMutation.mutate(data)
  })

  const handleDstChange = useCallback<OnChangeAmount>(
    amount => {
      setValue('dstAmount', amount, { shouldValidate: true })
    },
    [setValue],
  )

  const handlePromoChange = useCallback<OnChangePromo>(
    code => {
      setValue('promoCode', code, { shouldValidate: true })
    },
    [setValue],
  )

  return (
    <BuyContext.Provider
      value={{
        src_currency,
        submitIsLoading: submitMutation.isLoading,
        handleSubmit,
        handleBillingNextSubmit,
        handleDstChange,
        handlePromoChange,
        form,
      }}
    >
      {children}
    </BuyContext.Provider>
  )
}

export const useBuyContext = () => useContextWithCheck(BuyContext)
